import React from 'react';

import { useNavigate } from 'react-router-dom';

import {
    AssetDetails,
    Button,
    Modal,
    ModalCloseButtonPositions,
    ModalPaddingSizes,
    ModalVariants,
    Price,
} from '@pkg/components';
import { ModalHeaderAlignments } from '@pkg/components/Modal/components';
import { useAppSelector } from '@pkg/hooks';
import { type MarketplaceBuyAsset } from '@pkg/types';
import { trimDecimals } from '@pkg/utils';

import { Currency } from '@app/client/common/structs/nft/currencies';
import { getNftDetailsUrl } from '@app/helpers/url';
import { useExchangeRatesStore } from '@app/store/zustand';

import {
    ActionLink,
    Actions,
    ActionTile,
    ActionTitle,
    Summary,
    SummaryCount,
    SummaryHeader,
    TitleLarge,
    TitleMedium,
    Total,
    Totals,
    TotalValue,
} from './AssetBuyModal.style';

export interface AssetBuyModalProps {
    asset: MarketplaceBuyAsset;
    type?: 'preBuy' | 'postBuy';
    onClose: () => void;
}

// TODO: Update all strings to use `i18n`.
export function AssetBuyModal({ asset, type = 'preBuy', onClose }: AssetBuyModalProps) {
    const navigate = useNavigate();
    const primaryYat = useAppSelector((state) => state.user?.primaryYat);

    const convertCurrency = useExchangeRatesStore((state) => state.convertCurrency);

    const usdPrice = convertCurrency(Currency.ETH, Currency.USD, parseFloat(asset?.price?.value || '0'));

    function handleViewAsset() {
        onClose();

        const nftDetailsUrl = getNftDetailsUrl({
            contractAddress: asset.collection.contractAddress,
            tokenId: asset.tokenId,
        });

        navigate(nftDetailsUrl);
    }

    function handleViewProfile() {
        onClose();
        const profilePath = primaryYat ? `${primaryYat}` : 'profile';
        navigate(`/${profilePath}`);
    }

    const preBuyMarkup = (
        <>
            <Summary>
                <SummaryHeader>
                    <TitleMedium>Summary</TitleMedium>
                    <SummaryCount>1 Item</SummaryCount>
                </SummaryHeader>

                <AssetDetails
                    key={asset.name}
                    name={asset.name}
                    collectionName={asset.collection.name}
                    imageUrl={asset.imageUrl}
                />
            </Summary>

            <Totals>
                <Total>
                    <TitleMedium>Subtotal</TitleMedium>
                    <TotalValue>
                        <Price
                            currency={asset.price?.currency || 'eth'}
                            currencyPrice={parseFloat(asset.price?.value || '0')}
                            usdPrice={trimDecimals(usdPrice)}
                        />
                    </TotalValue>
                </Total>
                {/* TODO: Update gas fees */}
                {/* <CartSummary
                    items={[
                        {
                            label: 'Gas fees',
                            value: '$75.15',
                        },
                    ]}
                /> */}
            </Totals>

            <ActionTile>
                <ActionTitle>Go to your wallet</ActionTitle>
                <div>You&apos;ll be asked to approve this purchase from your wallet.</div>
            </ActionTile>
        </>
    );

    const postBuyMarkup = (
        <>
            <ActionTile key={asset.name}>
                <AssetDetails
                    name={asset.name}
                    collectionName={asset.collection.name}
                    imageUrl={asset.imageUrl}
                    size="large"
                />
            </ActionTile>
            <Actions>
                <Button size="large" onClick={handleViewAsset}>
                    View your asset
                </Button>
                <ActionLink onClick={handleViewProfile}>View on profile</ActionLink>
            </Actions>
        </>
    );

    return (
        <Modal
            variant={ModalVariants.Simple}
            closeButtonPosition={ModalCloseButtonPositions.Header}
            onClose={onClose}
            padded={true}
            paddingSize={ModalPaddingSizes.Large}
        >
            <Modal.Header align={ModalHeaderAlignments.Left}>
                <TitleLarge>{type === 'postBuy' ? 'Your Purchase is Complete' : 'Approve Purchase'}</TitleLarge>
            </Modal.Header>

            <Modal.Layout>
                <Modal.Layout.Section textAlign="left">
                    {type === 'postBuy' ? postBuyMarkup : preBuyMarkup}
                </Modal.Layout.Section>
            </Modal.Layout>
        </Modal>
    );
}
